import React from 'react';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import EmptyState from '@atlaskit/empty-state';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { ContactInfo, InvoiceGroup } from '@css-xp/graphtypes/src/generated-types-new';
import { getIsEnabledBasedOnThePaymentMethod } from '../../../order-summary/src/components/change-billing-profile';
import EmptyBillingProfileIcon from '../../../assets/empty-billing-profile-icon.svg';
import { BillingProfileCard } from './billing-profile-card';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';

export interface BillingProfilePanelProps {
  billingProfiles: InvoiceGroup[];
  primaryContacts: ContactInfo[];
  isPaypalEnabled: boolean;
  onSelectBillingProfile?: (_billingProfileId: string) => void;
  openCreateBillingProfile?: () => void;
  shouldShowACHPaymentMethod?: boolean;
}

const billingProfilesHeading = xcss({
  paddingTop: 'space.250',
  paddingBottom: 'space.150',
});

const StyledBillingProfileHeader = styled.span`
  font-weight: ${token('font.weight.bold')};
`;

const getEligibleAndIneligibleBillingProfiles = (billingProfiles: InvoiceGroup[], isPaypalEnabled: boolean) => {
  const eligibleBillingProfiles: InvoiceGroup[] = [];
  const ineligibleBillingProfiles: InvoiceGroup[] = [];

  billingProfiles?.forEach((billingProfile) => {
    const isEligible = getIsEnabledBasedOnThePaymentMethod({
      billingProfile,
      isDeferredPaymentMethodDisabled: true,
      isNoPaymentMethodDisabled: false,
      isPaypalEnabled,
    });

    if (isEligible) {
      eligibleBillingProfiles.push(billingProfile);
    } else {
      ineligibleBillingProfiles.push(billingProfile);
    }
  });
  return { eligibleBillingProfiles, ineligibleBillingProfiles };
};

export const BillingProfilePanelV2 = ({
  billingProfiles,
  primaryContacts,
  isPaypalEnabled,
  onSelectBillingProfile,
  openCreateBillingProfile,
  shouldShowACHPaymentMethod,
}: BillingProfilePanelProps) => {
  const { eligibleBillingProfiles, ineligibleBillingProfiles } = getEligibleAndIneligibleBillingProfiles(
    billingProfiles,
    isPaypalEnabled
  );

  const hasNoBillingProfiles = billingProfiles?.length === 0;

  const renderEmptyBillingProfileState = (ineligibleBillingProfilesAvailable?: boolean) => {
    return (
      <Flex direction="column" justifyContent="center">
        <EmptyState
          header="No billing profile"
          description={`This customer has no ${
            ineligibleBillingProfilesAvailable ? 'eligible ' : ''
          }billing profile yet. Create one now if you want to continue the order.`}
          imageUrl={EmptyBillingProfileIcon as any}
          primaryAction={
            <AdvocateAccessWall
              accessType={ADVOCATE_ACCESS_TYPE.CREATE}
              resource={ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP}
            >
              <Button appearance="primary" onClick={openCreateBillingProfile}>
                Create billing profile
              </Button>
            </AdvocateAccessWall>
          }
        />
      </Flex>
    );
  };

  return (
    <>
      {hasNoBillingProfiles && renderEmptyBillingProfileState()}
      {!hasNoBillingProfiles &&
        (eligibleBillingProfiles?.length > 0 ? (
          <>
            <Box xcss={billingProfilesHeading}>
              <StyledBillingProfileHeader>Eligible billing profiles : Select one</StyledBillingProfileHeader>
            </Box>
            {eligibleBillingProfiles.map((billingProfile: InvoiceGroup) => {
              const primaryCustomerContact = primaryContacts?.find(
                (contact) => contact?.identifierId === billingProfile?.id
              );
              return (
                <BillingProfileCard
                  key={billingProfile.id}
                  billingProfile={billingProfile}
                  primaryCustomerContact={primaryCustomerContact as any}
                  onSelectBillingProfile={onSelectBillingProfile}
                  shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
                />
              );
            })}
          </>
        ) : (
          renderEmptyBillingProfileState(true)
        ))}
      {ineligibleBillingProfiles?.length > 0 && (
        <>
          <Box xcss={billingProfilesHeading}>
            <Flex alignItems="center" gap="space.100">
              <StyledBillingProfileHeader>Unavailable</StyledBillingProfileHeader>
              <Badge>{ineligibleBillingProfiles?.length}</Badge>
            </Flex>
            <Text>
              Payment method, price eligibility or currency can make a billing profile non-eligible for an order.
            </Text>
          </Box>
          {ineligibleBillingProfiles.map((billingProfile: InvoiceGroup) => {
            const primaryCustomerContact = primaryContacts?.find(
              (contact) => contact?.identifierId === billingProfile?.id
            );
            return (
              <BillingProfileCard
                key={billingProfile.id}
                billingProfile={billingProfile}
                primaryCustomerContact={primaryCustomerContact as any}
                isInEligible={true}
                onSelectBillingProfile={onSelectBillingProfile}
              />
            );
          })}
        </>
      )}
    </>
  );
};
